import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { useNavigate } from 'react-router';
import graphql from 'babel-plugin-relay/macro';

import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import ColumnEditor from 'components/ColumnEditor';
import CateringOptionName from 'components/CateringOptionName';

import { useDialog } from 'components/Dialog';
import { useMenu } from 'components/IconButtonMenu';
import { useErrorHandler } from 'lib';

import { RoomRatesEditorCateringSettings_data$key } from '__generated__/RoomRatesEditorCateringSettings_data.graphql';
import { RoomRatesEditorCateringSettings_package$key } from '__generated__/RoomRatesEditorCateringSettings_package.graphql';
import { RoomRatesEditorCateringSettingsMutation } from '__generated__/RoomRatesEditorCateringSettingsMutation.graphql';

type Props = {
  data: RoomRatesEditorCateringSettings_data$key;
  package: RoomRatesEditorCateringSettings_package$key;
};

type FilterableItem = {
  name: string | null;
  etgKey: string | null;
};

const filter = (item: FilterableItem, search: string) =>
  (item.name?.toLowerCase().includes(search) ||
    item.etgKey?.toLowerCase().includes(search)) ??
  search.length === 0;

const RoomRatesEditorCateringSettings: FC<Props> = props => {
  const { close: closeDialog } = useDialog();
  const { close: closeMenu } = useMenu();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();

  const { allCateringOptions } = useFragment(
    graphql`
      fragment RoomRatesEditorCateringSettings_data on Query {
        allCateringOptions {
          id
          name
          etgKey

          ...CateringOptionName_cateringOption
        }
      }
    `,
    props.data
  );

  const pack = useFragment(
    graphql`
      fragment RoomRatesEditorCateringSettings_package on Package {
        id

        cateringOptions {
          id
        }
      }
    `,
    props.package
  );

  const [update, updating] =
    useMutation<RoomRatesEditorCateringSettingsMutation>(graphql`
      mutation RoomRatesEditorCateringSettingsMutation(
        $id: ID!
        $input: UpdatePackageInput!
      ) {
        updatePackage(id: $id, input: $input) {
          ...PackageEditAccommodation_package
        }
      }
    `);

  const handleSaveClick = useCallback(
    (values: string[]) => {
      update({
        variables: {
          id: pack.id,
          input: {
            cateringOptions: values.map((id, position) => ({ id, position })),
          },
        },

        ...errorHandler({
          onCompleted: () => {
            closeDialog();
            closeMenu();
          },
        }),
      });
    },
    [closeDialog, closeMenu, errorHandler, pack.id, update]
  );

  const handleEditCateringOptionsClick = useCallback(() => {
    navigate('/settings/catering');
  }, [navigate]);

  return (
    <ColumnEditor
      title="Maitinimo nustatymai"
      noItemsText="Nėra maitinimo pasirinkimų"
      noItemsAction={
        <Button
          color="inherit"
          size="small"
          onClick={handleEditCateringOptionsClick}
          startIcon={<EditIcon />}>
          Redaguoti sąrašą
        </Button>
      }
      loading={updating}
      items={allCateringOptions}
      initial={pack.cateringOptions.map(({ id }) => id)}
      filter={filter}
      renderItem={option => <CateringOptionName cateringOption={option} />}
      onClose={closeMenu}
      onSave={handleSaveClick}
    />
  );
};

export default RoomRatesEditorCateringSettings;
